import { Box, Center, Flex } from '@chakra-ui/react';
import React from 'react';

import {
  getContentBackgroundColor,
  getFlexBreakpoint,
  getFlexDirection,
  getHeight,
} from './hero.utils';
import HeroContent from './HeroContent';
import HeroImage from './HeroImage';
import {
  HeroBackground,
  HeroContentPosition,
  HeroSize,
  HeroTitleVariant,
} from './index';

interface HeroProps {
  title?: {
    value: string | null | undefined;
    variant: HeroTitleVariant;
  };
  content?: {
    value: React.ReactElement;
    position: HeroContentPosition;
    widthPercent?: number;
  };
  image: StaticImageDef | React.ReactNode;
  navbar: React.ReactNode;
  background: HeroBackground;
  size?: HeroSize;
  overlay: boolean;
  children?: never;
  wrapContentBottom?: boolean;
}

const DEFAULT_SPLIT_SIZE = 60;

const Hero: React.FC<HeroProps> = ({
  image,
  title,
  content,
  navbar,
  overlay,
  background,
  size = 'medium',
  wrapContentBottom,
}) => {
  const effectiveSplit = content
    ? content.widthPercent || DEFAULT_SPLIT_SIZE
    : 0;
  const height = getHeight(size);
  const flexBreakpoint = getFlexBreakpoint(size);
  const flexDirection = getFlexDirection(content?.position || 'left', overlay);
  const contentBasis = `${effectiveSplit}%`;
  const imageBasis = `${100 - effectiveSplit}%`;

  // When image wraps allow it to expand vertically to maintain aspect ratio and avoid cropping, but when inline force
  // to the same height as the content block
  const imageHeight = {
    ...height,
    base: 'auto',
    sm: 'auto',
    md: flexBreakpoint === 'lg' ? 'auto' : height.md,
  };

  const hero = (
    <Flex
      width="full"
      height="full"
      position="relative"
      flexDirection={{
        base: wrapContentBottom ? 'column' : 'column-reverse',
        [flexBreakpoint]: flexDirection,
      }}
    >
      <Box
        width="full"
        height="full"
        flexBasis={overlay ? undefined : imageBasis}
      >
        <HeroImage image={image} height={imageHeight} />
      </Box>
      {content && (
        <Box
          flexBasis={contentBasis}
          width={overlay ? { lg: contentBasis } : undefined}
          position={overlay ? { lg: 'absolute' } : undefined}
        >
          <HeroContent
            title={title}
            height={height}
            overlay={overlay}
            position={content.position}
            background={background}
          >
            {content.value}
          </HeroContent>
        </Box>
      )}
    </Flex>
  );

  if (navbar) {
    return (
      <Flex direction="column" width="full">
        {hero}
        <Center
          width="full"
          backgroundColor={getContentBackgroundColor(background)}
        >
          {navbar}
        </Center>
      </Flex>
    );
  }
  return hero;
};

export default Hero;
